import { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { formatCents, parseDollarsToCents } from '@/utils/money';
import { goalProgress, readSavingsGoal, writeSavingsGoal, type SavingsGoal } from './savingsGoal';

const PRESETS = [500, 1000, 2000, 5000];

/* The prototype's savings-goal block: a target, a bar, "$X to go". Progress is
   measured on LOCKED SAVINGS — the bucket that holds preserved losses — which
   is what a goal here is about. */
export function SavingsGoalCard({ userId, savingsCents }: { userId: string | undefined; savingsCents: number }) {
  const [goal, setGoal] = useState<SavingsGoal | null>(() => readSavingsGoal(userId));
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState('');
  const [label, setLabel] = useState('');

  const open = () => {
    setAmount(goal ? (goal.targetCents / 100).toFixed(0) : '');
    setLabel(goal?.label ?? '');
    setEditing(true);
  };
  const save = () => {
    const cents = parseDollarsToCents(amount || '0');
    if (!Number.isFinite(cents) || cents <= 0) return;
    const next = { targetCents: cents, label: label.trim() };
    writeSavingsGoal(userId, next);
    setGoal(next);
    setEditing(false);
  };
  const remove = () => {
    writeSavingsGoal(userId, null);
    setGoal(null);
    setEditing(false);
  };

  const progress = goal ? goalProgress(savingsCents, goal) : null;

  return (
    <Card title="Savings goal">
      {goal && progress ? (
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-sm text-white/55">{goal.label ? goal.label : 'Progress to goal'}</div>
            <div className="text-[15px] font-bold tabular-nums">
              {formatCents(savingsCents)} <span className="text-xs font-normal text-white/45">of {formatCents(goal.targetCents)}</span>
            </div>
          </div>
          <div className="h-2.5 rounded-full bg-white/[0.06] overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-brand-green to-brand-mint transition-all duration-500" style={{ width: `${progress.pct}%` }} />
          </div>
          <div className="flex justify-between text-xs text-white/45 mt-1.5">
            <span>{progress.pct}% reached</span>
            <span>{formatCents(progress.toGoCents)} to go</span>
          </div>
          {progress.reached && <div className="mt-2 text-sm font-semibold text-brand-green">🎉 You've reached your goal!</div>}
          <button onClick={open} className="mt-3 text-xs text-brand-teal hover:brightness-110">Edit goal</button>
        </div>
      ) : (
        <div className="text-center py-2">
          <p className="text-sm text-white/55 leading-relaxed mb-3">Set a savings goal to track your locked Savings toward a target — a trip, a big purchase, anything.</p>
          <Button onClick={open}>Set a goal</Button>
        </div>
      )}

      <Modal
        open={editing}
        title={goal ? 'Edit savings goal' : 'Set a savings goal'}
        onClose={() => setEditing(false)}
        footer={
          <div className="flex items-center justify-between gap-2 w-full">
            {goal ? <button onClick={remove} className="text-xs text-red-400 hover:brightness-110">Remove goal</button> : <span />}
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="sm" onClick={save} disabled={!(parseDollarsToCents(amount || '0') > 0)}>Save goal</Button>
            </div>
          </div>
        }
      >
        <label className="block text-xs text-white/50 mb-1.5">Target (AUD)</label>
        <input
          type="number" min="1" step="1" inputMode="numeric" placeholder="e.g. 2000" value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full bg-brand-navy2 border border-white/15 rounded-xl px-3.5 py-3 text-white text-lg font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-teal/50"
        />
        <div className="mt-2 flex gap-1.5">
          {PRESETS.map((d) => (
            <button key={d} type="button" onClick={() => setAmount(String(d))} className="flex-1 rounded-lg border border-white/[0.08] bg-white/[0.03] py-1.5 text-xs font-semibold text-white/60 hover:bg-white/[0.08] hover:text-white transition">
              ${d.toLocaleString('en-AU')}
            </button>
          ))}
        </div>
        <label className="block text-xs text-white/50 mt-4 mb-1.5">What's it for? (optional)</label>
        <input
          type="text" maxLength={40} placeholder="e.g. Bali trip" value={label}
          onChange={(e) => setLabel(e.target.value)}
          className="w-full bg-brand-navy2 border border-white/15 rounded-xl px-3.5 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/50"
        />
      </Modal>
    </Card>
  );
}
